<script lang="ts" setup>
import { useRelatedSchematics } from "@/abilities/schematic-viewer/composables/use-related-schematics.ts";
import { computed } from "vue";
// import { MAX_WIDGET_CARDS } from "@/shared/components/widgets/widget.constants";
import ListCard from "@/shared/components/card/list-card.vue";
import QitRouterLink from "@/shared/components/router-link/qit-router-link.vue";
import WidgetHeader from "@/shared/components/widgets/widget-header.vue";
import { useAbilityRoute } from "@/shared/environment/ability-route";
import { CommonRouteAliases } from "@/shared/environment/common-route-aliases";
import { WidgetContext } from "@/shared/environment/widgets/widget-context";
import { useScreenSize } from "@/shared/screen/composables/screen-size.ts";
import { getSearchContextByWidgetContext } from "@/shared/utils/widget-utils.ts";

const props = defineProps<{
    widgetContext: WidgetContext;
}>();

const { getPathToChildrenWithoutParents, getRouteElementsBySearchContext } = useAbilityRoute();
const searchContext = computed(() => {
    return getSearchContextByWidgetContext(props.widgetContext);
});
const getSchematicPath = (schematicId: string) => {
    const routeElements = getRouteElementsBySearchContext(searchContext.value);
    routeElements.push({
        alias: CommonRouteAliases.schematicViewer,
        namedParams: { schemaId: schematicId, schemaPath: "-" },
    });
    return getPathToChildrenWithoutParents(routeElements);
};
const getSchematicsOverviewPath = () => {
    const routeElements = getRouteElementsBySearchContext(searchContext.value);
    routeElements.push({
        alias: CommonRouteAliases.schematics,
        namedParams: {},
    });
    return getPathToChildrenWithoutParents(routeElements);
};
const { isLessThanMedium } = useScreenSize();
const productId = computed(() => searchContext.value?.product);
const { loading, schematics, total } = useRelatedSchematics(productId);
const isWidgetVisible = computed(() => {
    return !loading.value && schematics.value && schematics.value.length > 0;
});
// Temporary business rule (QIT-7058): keep "View all schematics" visible for result count > 1
// until PO confirms whether MAX_WIDGET_CARDS gating should be restored.
const showViewAllLink = computed(() => total.value > 1);
</script>

<template>
    <div v-if="isWidgetVisible" id="related-schematics">
        <WidgetHeader :title="$t('schematic-viewer.Schematics')" icon="fa-regular fa-plug" class="q-mb-md">
            <template #right>
                <QitRouterLink
                    v-if="showViewAllLink"
                    data-qs="related-schematics-view-all-btn"
                    :to="getSchematicsOverviewPath()"
                    :label="$t('schematic-viewer.View all schematics')"
                    hover-background
                />
            </template>
        </WidgetHeader>
        <div data-qs="related-schematics-widget">
            <div class="row" :class="{ 'q-col-gutter-md': !isLessThanMedium, gutter: isLessThanMedium }">
                <div class="col-md-4 col-xs-12" v-for="(schematic, index) in schematics" :key="schematic.id">
                    <router-link :to="getSchematicPath(schematic.id)">
                        <ListCard
                            card-type="small"
                            class="schematic-card"
                            :title="schematic.title ?? undefined"
                            :locale-tag="schematic.lang ?? undefined"
                            :test-id="`related-schematics-${index}`"
                            fallback-icon="fa-plug"
                        />
                    </router-link>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.gutter {
    gap: 12px;
}
.schematic-card {
    height: 100%;
    display: flex;
    justify-content: flex-start;
}
</style>
